import { Fragment, useId, useRef } from "react";
import type { KeyboardEvent, ReactNode } from "react";

export interface SegmentedControlOption<V extends string = string> {
  value: V;
  label: ReactNode;
  /** Per-option disable (the group stays interactive). */
  disabled?: boolean;
  /** The reason — REQUIRED when `disabled` has one the user should see
   *  (honest-tooltip convention: the option exists, say why it is off).
   *  Rendered as the native tooltip AND as the segment's accessible
   *  description (`aria-describedby` → a visually hidden span), because
   *  `title` alone is not read reliably by screen readers. */
  title?: string;
  /** Per-option test hook (`data-testid` on the segment button). */
  testId?: string;
}

export interface SegmentedControlProps<V extends string = string> {
  options: readonly SegmentedControlOption<V>[];
  /** The selected value — exactly one segment is checked at a time. */
  value: V;
  /** Called with the clicked segment's value. NOT called for the already-
   *  selected segment or a disabled one. */
  onChange: (value: V) => void;
  /** Accessible name for the group (`radiogroup`) — REQUIRED: an unnamed
   *  radiogroup is announced as nothing (#838 iter-2, C m-6). */
  "aria-label": string;
  "data-testid"?: string;
  className?: string;
  /** Render every segment at the SAME width (that of the widest label),
   *  centered — an inline-grid with equal flexible columns, so the control
   *  still shrink-wraps its content. Layout-only: roles, keyboard model and
   *  per-segment disable/tooltip are identical either way. */
  equalWidth?: boolean;
}

/**
 * SegmentedControl — a mutually-exclusive action list (one-of-N), rendered as
 * a row of connected segments. Added 2026-09-05 for the unified conflict
 * action list (nu-assist conflict-actions v2); generic on purpose — nothing
 * in it knows about conflicts.
 *
 * Semantics: `role="radiogroup"` over `role="radio"` buttons, because the
 * control expresses a CHOICE with a persistent selected state — not N
 * independent commands. `aria-checked` carries the selection for AT; the
 * filled segment carries it visually.
 *
 * Design-system notes (v17 tokens only, no inline styles):
 *  - selected: accent fill + `text-bg` ink, same pair as `Button`'s primary
 *    variant, so "your current choice" reads with the system's one emphasis
 *    color;
 *  - unselected: transparent on `bg-bg`, secondary ink, hover raises to
 *    `bg-surface` — the quiet state;
 *  - disabled: `opacity-50` + `cursor-not-allowed`, and the `title` tooltip
 *    still answers "why" (aria-disabled keeps the element interactive so the tooltip fires; click/key guards enforce the off state).
 *    The same reason is the segment's accessible description, and a disabled
 *    segment stays reachable by Tab / arrows — even when every segment of the
 *    group is disabled.
 *
 * Clicking the selected segment is a no-op (no `onChange`): a radio group has
 * no "unselect".
 *
 * THIS CONTROL CANNOT RENDER AN UNSELECTED STATE, and that is a real
 * constraint, not a gap to paper over. `value` is `V`, so one segment is always
 * checked. Until 2026-09-10 this docstring told callers to model a default
 * option instead — "e.g. Do nothing" — and that advice built a neutral default
 * into the conflict cards where "nobody looked at this row" and "somebody
 * looked and chose to leave it alone" became the same value. fu5 sub-task 02
 * deleted that option, so the advice is deleted with it.
 *
 * IF YOUR CHOICE CAN BE UNANSWERED, USE `HighlightOptionGroup` instead: it
 * takes `value: V | null`, renders the unanswered state legally (nothing
 * `aria-checked`, `aria-required` on the group), and its options are
 * full-sentence rows rather than short segments. Reach for `SegmentedControl`
 * only when every state of the caller genuinely HAS an answer.
 */
export function SegmentedControl<V extends string = string>({
  options,
  value,
  onChange,
  className,
  equalWidth = false,
  ...rest
}: SegmentedControlProps<V>) {
  // FOCUS-THEN-ACTIVATE keyboard model (#838 iter-2, A-N1 + C-N2 + the C-2
  // residual, one remedy for all three): Arrow/Home/End move DOM FOCUS only —
  // they never select, because two of this control's real-world options are
  // one-shot persisted COMMANDS and selection-follows-focus would execute
  // them while the user is merely browsing. Space/Enter activates the
  // focused segment (same guard as click). Disabled segments ARE focusable
  // in the arrow order so their honest tooltip/title is keyboard-reachable;
  // activation on them is a no-op. The roving tab stop is the selected
  // segment (else the first enabled one, else the selected or first one) —
  // Tab enters the group there. The last fallback keeps a group whose every
  // option is off in the tab order: unavailable options stay focusable so a
  // keyboard or screen-reader user reaches them and hears why (owner
  // decision 2026-10-23, candidate-reports-four-kinds 02 — a backchannel-only
  // candidate's Assessments group had no Tab stop at all).
  const refs = useRef<(HTMLButtonElement | null)[]>([]);
  const reasonIdPrefix = useId();
  const selectedIdx = options.findIndex((o) => o.value === value);
  const firstEnabled = options.findIndex((o) => !o.disabled);
  const tabbableIdx =
    selectedIdx >= 0 && !options[selectedIdx]?.disabled
      ? selectedIdx
      : firstEnabled >= 0
        ? firstEnabled
        : Math.max(selectedIdx, 0);
  const focusAt = (idx: number) => refs.current[idx]?.focus();
  const onKeyDown = (e: KeyboardEvent, idx: number) => {
    const n = options.length;
    if (n === 0) return;
    let target: number | null = null;
    if (e.key === "ArrowRight" || e.key === "ArrowDown") target = (idx + 1) % n;
    else if (e.key === "ArrowLeft" || e.key === "ArrowUp") target = (idx - 1 + n) % n;
    else if (e.key === "Home") target = 0;
    else if (e.key === "End") target = n - 1;
    else if (e.key === " " || e.key === "Enter") {
      e.preventDefault();
      const opt = options[idx];
      if (opt && !opt.disabled && opt.value !== value) onChange(opt.value);
      return;
    }
    if (target === null) return;
    e.preventDefault();
    focusAt(target);
  };
  return (
    <div
      role="radiogroup"
      aria-label={rest["aria-label"]}
      data-testid={rest["data-testid"]}
      className={[
        // `equalWidth`: an inline-grid with one flexible column per segment.
        // Under a shrink-wrapped (indefinite-width) container every `1fr`
        // column resolves to the LARGEST max-content among them (CSS Grid's
        // used-flex-fraction rule), which is exactly "equal widths, none
        // narrower than its own label".
        equalWidth
          ? "inline-grid grid-flow-col auto-cols-fr items-stretch gap-px overflow-hidden rounded-md border border-border bg-bg p-0.5"
          : "inline-flex flex-wrap items-center gap-px overflow-hidden rounded-md border border-border bg-bg p-0.5",
        className,
      ]
        .filter(Boolean)
        .join(" ")}
    >
      {options.map((opt, idx) => {
        const selected = opt.value === value;
        const reasonId = opt.title ? `${reasonIdPrefix}-reason-${idx}` : undefined;
        return (
          <Fragment key={opt.value}>
            <button
              type="button"
              // biome-ignore lint/a11y/useSemanticElements: a native <input
              // type="radio"> cannot be disabled-with-tooltip per option nor
              // styled as a filled segment without a hidden-input+label
              // reimplementation of exactly this; button[role=radio] +
              // aria-checked inside role=radiogroup is the ARIA radio-group
              // pattern (same precedent as CompareToThriveModal's role=dialog).
              role="radio"
              aria-checked={selected}
              data-testid={opt.testId}
              // aria-disabled, NOT native disabled: a natively-disabled button
              // swallows pointer events in some browsers (tooltip dies) and
              // drops out of the tab order entirely — the honest tooltip and
              // the roving-focus model both need the element interactive
              // (pr-review #838 iter-1, C-2). The onClick/onKeyDown guards
              // enforce the non-interactivity.
              aria-disabled={opt.disabled || undefined}
              tabIndex={idx === tabbableIdx ? 0 : -1}
              ref={(el) => {
                refs.current[idx] = el;
              }}
              title={opt.title}
              aria-describedby={reasonId}
              className={[
                "rounded-sm px-2 py-1 text-label transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent",
                equalWidth ? "text-center" : "",
                selected
                  ? "bg-accent font-semibold text-bg"
                  : "bg-transparent text-text-secondary hover:bg-surface hover:text-text",
                opt.disabled
                  ? "cursor-not-allowed opacity-50 hover:bg-transparent hover:text-text-secondary"
                  : "",
              ].join(" ")}
              onClick={() => {
                if (!selected && !opt.disabled) onChange(opt.value);
              }}
              onKeyDown={(e) => onKeyDown(e, idx)}
            >
              {opt.label}
            </button>
            {reasonId ? (
              // Outside the button so it is the DESCRIPTION, not part of the
              // name; `sr-only` is absolutely positioned, so it takes no flex
              // slot nor grid column (`equalWidth`).
              <span id={reasonId} className="sr-only">
                {opt.title}
              </span>
            ) : null}
          </Fragment>
        );
      })}
    </div>
  );
}
